import React, { useEffect } from "react";
import { useSelector } from "react-redux";
import { Box, Button, Typography } from "@mui/material";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import IndexApp from "../../Components/IndexApp";
import Layout from "../../Components/Layout";
import HomeHeader from "../../Components/Ui/HomeHeader";
import NotificationList from "../../Components/AppBar/NotificationList";
import { useTailwindBrand } from "../../Components/shared";
import useNotifications from "../../Hooks/useNotifications";

/**
 * Page « Notifications » du tableau de bord commun (prof et client) : toutes les
 * notifications de l'utilisateur connecté, avec les mêmes filtres et le même
 * regroupement que le panneau de la cloche, « Tout marquer comme lu » et « Voir plus ».
 * L'état est celui du store partagé de useNotifications : la page ne recharge que si
 * la liste est absente ou périmée.
 */
const NotificationsPage = () => {
    const { brandConfig } = useTailwindBrand();
    const currentUser = useSelector((state: any) => state?.user?.currentUser);
    const account = currentUser?.isClient ? currentUser?.client : currentUser?.teachr;
    const notifications = useNotifications();
    const { enabled, items, unreadCount, total, loaded, refresh, markAllAsRead } = notifications;
    const primary = brandConfig.colors.primary;
    const hasUnread = unreadCount > 0 || items.some((item) => !item.isRead);

    useEffect(() => {
        if (enabled) refresh();
    }, [enabled, refresh]);

    const subtitle = unreadCount > 0
        ? `${unreadCount} notification${unreadCount > 1 ? "s" : ""} non lue${unreadCount > 1 ? "s" : ""}.`
        : "Vous êtes à jour : aucune notification non lue.";

    return (
        <IndexApp
            navBar={{ index: 0 }}
            teachr={account}
            component={
                <Layout header={<HomeHeader title="🔔 Notifications" subtitle={subtitle} />}>
                    <div className="bg-white rounded-md p-4">
                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2, flexWrap: "wrap", mb: 1 }}>
                            <Typography variant="body2" sx={{ color: "text.secondary", fontFamily: brandConfig.fontFamily }}>
                                {loaded
                                    ? `${total} notification${total > 1 ? "s" : ""}`
                                    : "Chargement de vos notifications…"}
                            </Typography>
                            <Button
                                size="small"
                                onClick={markAllAsRead}
                                disabled={!hasUnread}
                                startIcon={<DoneAllIcon sx={{ fontSize: "1rem !important" }} />}
                                sx={{
                                    textTransform: "none",
                                    fontWeight: 600,
                                    fontSize: "0.85rem",
                                    fontFamily: brandConfig.fontFamily,
                                    color: primary,
                                    borderRadius: "10px",
                                    "&:hover": { backgroundColor: `${primary}0f` },
                                }}
                            >
                                Tout marquer comme lu
                            </Button>
                        </Box>
                        <Box sx={{ border: "1px solid rgba(0,0,0,0.06)", borderRadius: "16px", overflow: "hidden" }}>
                            <NotificationList notifications={notifications} />
                        </Box>
                    </div>
                </Layout>
            }
        />
    );
};

export default NotificationsPage;
